
import { A2AMessage, A2AAgent, A2AOrchestrationPlan } from '../types';

// Success metrics for different orchestration patterns
type OrchestrationMetrics = {
  patternId: string;
  successRate: number;
  averageCompletionTime: number;
  patternType: 'sequential' | 'parallel' | 'hierarchical';
  lastUpdated: Date;
  sampleSize: number;
};

// In-memory store for orchestration performance data
// In a production system, this would be persisted to a database
const orchestrationPerformance: Record<string, OrchestrationMetrics> = {};

/**
 * Updates the performance metrics for an orchestration pattern
 */
export function recordOrchestrationResult(
  planId: string,
  patternType: 'sequential' | 'parallel' | 'hierarchical',
  wasSuccessful: boolean,
  completionTimeMs: number
): void {
  const patternKey = `${patternType}-${planId.split('-')[0]}`;
  
  if (!orchestrationPerformance[patternKey]) {
    orchestrationPerformance[patternKey] = {
      patternId: patternKey,
      successRate: wasSuccessful ? 1 : 0,
      averageCompletionTime: completionTimeMs,
      patternType,
      lastUpdated: new Date(),
      sampleSize: 1
    };
  } else {
    const current = orchestrationPerformance[patternKey];
    const newSampleSize = current.sampleSize + 1;
    
    // Update success rate
    const newSuccessCount = (current.successRate * current.sampleSize) + (wasSuccessful ? 1 : 0);
    const newSuccessRate = newSuccessCount / newSampleSize;
    
    // Update average completion time
    const newAvgTime = ((current.averageCompletionTime * current.sampleSize) + completionTimeMs) / newSampleSize;
    
    orchestrationPerformance[patternKey] = {
      ...current,
      successRate: newSuccessRate,
      averageCompletionTime: newAvgTime,
      lastUpdated: new Date(),
      sampleSize: newSampleSize
    };
  }
  
  console.log(`Updated orchestration metrics for ${patternKey}:`, orchestrationPerformance[patternKey]);
}

/**
 * Determines the best orchestration pattern for a given task based on historical performance
 */
export function determineOptimalPattern(
  requiredCapabilities: string[],
  taskComplexity: 'simple' | 'medium' | 'complex' = 'medium'
): 'sequential' | 'parallel' | 'hierarchical' {
  // Default patterns based on task complexity
  const defaultPattern: Record<string, 'sequential' | 'parallel' | 'hierarchical'> = {
    simple: 'sequential',
    medium: requiredCapabilities.length <= 2 ? 'sequential' : 'parallel',
    complex: 'hierarchical'
  };
  
  // Get all relevant performance records
  const relevantPatterns = Object.values(orchestrationPerformance).filter(
    metrics => metrics.sampleSize >= 3 // Only consider patterns with enough data
  );
  
  if (relevantPatterns.length === 0) {
    return defaultPattern[taskComplexity];
  }
  
  // Sort by success rate first, then by completion time
  relevantPatterns.sort((a, b) => {
    if (Math.abs(a.successRate - b.successRate) > 0.1) {
      return b.successRate - a.successRate; // Higher success rate first
    }
    return a.averageCompletionTime - b.averageCompletionTime; // Faster completion time second
  });
  
  // Choose the best pattern
  return relevantPatterns[0].patternType;
}

/**
 * Analyzes past execution results to improve future orchestration
 */
export function analyzeOrchestrationResults(plan: A2AOrchestrationPlan): {
  insights: string[];
  suggestedImprovements: string[];
} {
  const insights: string[] = [];
  const suggestedImprovements: string[] = [];
  
  // Analyze the current plan performance
  const success = plan.status === 'completed';
  const executionTime = plan.completedAt && plan.createdAt 
    ? plan.completedAt.getTime() - plan.createdAt.getTime() 
    : 0;
  
  if (executionTime > 5000) {
    insights.push(`Execution took ${executionTime}ms, which is longer than optimal.`);
    suggestedImprovements.push('Consider parallelizing more tasks for faster execution.');
  }
  
  if (!success) {
    insights.push('Orchestration plan failed to complete successfully.');
    suggestedImprovements.push('Consider adding redundancy for critical capabilities.');
  }
  
  // Add agent-specific insights
  if (plan.agentSequence.length > 3) {
    insights.push(`Plan used ${plan.agentSequence.length} agents, which may be inefficient.`);
    suggestedImprovements.push('Consider using agents with multiple capabilities to reduce handoffs.');
  }
  
  return {
    insights,
    suggestedImprovements
  };
}

/**
 * Adjusts the orchestration plan based on real-time feedback
 */
export function adaptOrchestrationInRealTime(
  plan: A2AOrchestrationPlan,
  messages: A2AMessage[],
  availableAgents: A2AAgent[]
): A2AOrchestrationPlan {
  // Clone the plan to avoid mutating the original
  const updatedPlan = { ...plan };
  
  // Check for stalled or slow agents
  const agentResponseTimes: Record<string, number> = {};
  
  messages.forEach(msg => {
    if (msg.metadata?.processingTime && msg.fromAgent) {
      agentResponseTimes[msg.fromAgent] = (agentResponseTimes[msg.fromAgent] || 0) + 
        Number(msg.metadata.processingTime);
    }
  });
  
  // Find bottlenecks - agents taking much longer than others
  const responseTimeValues = Object.values(agentResponseTimes);
  if (responseTimeValues.length < 2) return updatedPlan;
  
  const avgResponseTime = responseTimeValues.reduce((sum, time) => sum + time, 0) / 
    responseTimeValues.length;
  
  const bottlenecks = Object.entries(agentResponseTimes)
    .filter(([_, time]) => time > avgResponseTime * 1.5)
    .map(([agentId]) => agentId);
  
  // If we found bottlenecks, adjust the plan
  if (bottlenecks.length > 0) {
    console.log(`Detected bottlenecks in agents: ${bottlenecks.join(', ')}`);
    
    // Try to find alternative agents for the bottlenecked capabilities
    const bottleneckedAgentCapabilities = bottlenecks.map(agentId => {
      const agent = availableAgents.find(a => a.id === agentId);
      return agent ? agent.capabilities : [];
    }).flat();
    
    // Find alternative agents that aren't in the bottleneck list
    const alternativeAgents = availableAgents.filter(agent => 
      !bottlenecks.includes(agent.id) &&
      agent.isAvailable &&
      agent.capabilities.some(cap => bottleneckedAgentCapabilities.includes(cap))
    );
    
    if (alternativeAgents.length > 0) {
      // Replace bottlenecked agents with alternatives where possible
      updatedPlan.agentSequence = updatedPlan.agentSequence.map(agentId => {
        if (bottlenecks.includes(agentId)) {
          const original = availableAgents.find(a => a.id === agentId);
          if (!original) return agentId;
          
          const alternative = alternativeAgents.find(a => 
            a.capabilities.some(cap => original.capabilities.includes(cap))
          );
          
          return alternative ? alternative.id : agentId;
        }
        return agentId;
      });
      
      console.log('Adjusted orchestration plan to mitigate bottlenecks');
    }
  }
  
  return updatedPlan;
}

/**
 * Gets metrics for all orchestration patterns
 */
export function getOrchestrationMetrics(): OrchestrationMetrics[] {
  return Object.values(orchestrationPerformance);
}

/**
 * Gets metrics for a specific orchestration pattern
 */
export function getPatternMetrics(patternType: 'sequential' | 'parallel' | 'hierarchical'): OrchestrationMetrics[] {
  return Object.values(orchestrationPerformance).filter(
    metric => metric.patternType === patternType
  );
}
